import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import LoaderSpinner from "@components-shared/LoaderSpinner";
import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import { useRootAppStore } from "@hooks";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { PlayerSowStoreContext } from "@state/context/sow/PlayerSowStoreContext";
import PlayerSowStore from "@state/stores/sow/PlayerSowStore";
import { setTabTitle } from "@utils";
import { redirectToLogin } from "@utils/auth";

import {
	SowMessageDisplay,
	SowSubmissionDocumentsOnly,
	SowSubmissionForm,
} from "./components";

export const PlayerSowPage = observer(function PlayerSowPage() {
	const { t } = useTranslation();
	const { search } = useLocation();

	const rootStore = useRootAppStore();

	useScrollRestoration({ type: ConfigType.Basic });

	const playerSowStore = useMemo(() => new PlayerSowStore(rootStore), []);

	useEffect(() => {
		setTabTitle(t("PLAYER_SOW.TITLE"));

		const queryParams = new URLSearchParams(search);

		const params: Record<string, string> = {};

		queryParams.forEach((value, key) => {
			params[key] = value;
		});

		window.scrollTo(0, 0);
		if (!rootStore.userAuthStore.isLoggedIn) {
			redirectToLogin();
		}

		if (rootStore.userAuthStore.isLoggedIn) {
			onStoreInitialize(params.requestId, params.userId);
		}

		return () => {
			playerSowStore.onDispose();
		};
	}, []);

	const onStoreInitialize = async (requestId: string, userId: string) => {
		await playerSowStore.onInitialize(requestId, userId);
	};

	return (
		<DesktopLayout headerType={HeaderType.WITH_SIDEBAR}>
			<section className="l--content l--content--full">
				<div className="l--content__main">
					<div className="container">
						{playerSowStore.loader.isLoading && (
							<LoaderSpinner isLoading />
						)}
						<PlayerSowStoreContext.Provider value={playerSowStore}>
							{playerSowStore.isSubmissionRequired && (
								<>
									{playerSowStore.isFormRequired && (
										<SowSubmissionForm />
									)}
									{!playerSowStore.isFormRequired && (
										<SowSubmissionDocumentsOnly />
									)}
								</>
							)}
							{!playerSowStore.isSubmissionRequired && (
								<SowMessageDisplay />
							)}
						</PlayerSowStoreContext.Provider>
					</div>
				</div>
			</section>
		</DesktopLayout>
	);
});
